import type {
  Absence,
  Correction,
  Finding,
  ProjectAttributionRequestItem,
  ProjectDays,
  WorkDaysResponse,
} from "../../../api/schemas";
import type { BalancesWithBookings } from "../../../api/timeAccountSchemas";
import { routePaths } from "../../../app/navigation";
import { addDays } from "../../../domain/time";
import type { LocalDate } from "../../../domain/time";
import { openAbsenceRequestRange } from "../../absences/model/openRequests";
import type { DateRange } from "../../times/model/period";

/** Days the start page looks back for missing project attributions, like „Meine Zeiten“. */
export const openProjectDaysLookBack = 31;

/**
 * Returns the range of own absence requests the start page counts: like the open requests of the
 * manager view a month back (requests filed afterwards for past days, F-78) and up to 335 days
 * ahead, within the 366 days the server lists at most.
 *
 * @param today - Today in the time zone of the employment.
 * @returns The range.
 */
export function absenceRequestRange(today: LocalDate): DateRange {
  const range = openAbsenceRequestRange(today);

  return range;
}

/**
 * Returns the range of the reminder list of missing project attributions (T-125).
 *
 * @param today - Today in the time zone of the employment.
 * @returns The range.
 */
export function openProjectDayRange(today: LocalDate): DateRange {
  const range: DateRange = { from: addDays(today, -openProjectDaysLookBack), to: today };

  return range;
}

/** Kind of an open item on the start page. */
export type OpenItemKind =
  "corrections" | "absences" | "attributionRequests" | "findings" | "projectDays";

/** Something that waits for the person or for a decision, with the page that shows it. */
export interface OpenItem {
  /** What is open. */
  readonly kind: OpenItemKind;
  /** How many. */
  readonly count: number;
  /** The page that lists them. */
  readonly path: string;
}

/** The answers of the existing queries the open items come from; `undefined` while unknown. */
export interface OpenItemSources {
  /** The person's own employee identifier from `GET /me`, `null` while unknown. */
  readonly employeeId: string | null;
  /** `GET /corrections` (own and, for managers, the team's). */
  readonly corrections: readonly Correction[] | undefined;
  /** `GET /absences` of the person in {@link absenceRequestRange}. */
  readonly absences: readonly Absence[] | undefined;
  /**
   * `GET /project-attribution-requests?pendingOnly=true` (own and those the caller may decide), or
   * `undefined` while unknown or while project time tracking is off.
   */
  readonly attributionRequests: readonly ProjectAttributionRequestItem[] | undefined;
  /** `GET /me/workdays` of the current week. */
  readonly workDays: WorkDaysResponse | undefined;
  /** `GET /me/project-days` of the reminder range. */
  readonly projectDays: ProjectDays | undefined;
}

/**
 * Returns whether a finding asks for attention: everything except plain information.
 *
 * @param finding - The finding.
 * @returns `true` for warnings, violations and incomplete input.
 */
function needsAttention(finding: Finding): boolean {
  const attention = finding.severity !== "Info";

  return attention;
}

/**
 * Counts what is open for the person (F-75, F-78): own correction requests, own absence requests
 * (also those filed afterwards for past days) and own requests for project attributions in locked
 * periods waiting for a decision, findings of the current week, and closed days still lacking a
 * project attribution under the requirement. Only items with a count above zero are returned, in this
 * order; a source that is not known yet contributes nothing. Requests the person filed for
 * somebody else and the team's requests are not counted.
 *
 * @param sources - The answers of the queries.
 * @returns The open items.
 */
export function openItems(sources: OpenItemSources): OpenItem[] {
  const { employeeId } = sources;
  const ownCorrections =
    employeeId === null || sources.corrections === undefined
      ? 0
      : sources.corrections.filter(
          (correction) => correction.employeeId === employeeId && correction.status === "Pending",
        ).length;
  const ownAbsences =
    employeeId === null || sources.absences === undefined
      ? 0
      : sources.absences.filter(
          (absence) => absence.employeeId === employeeId && absence.status === "Requested",
        ).length;
  const ownAttributionRequests =
    employeeId === null || sources.attributionRequests === undefined
      ? 0
      : sources.attributionRequests.filter(
          (request) => request.employeeId === employeeId && request.status === "Pending",
        ).length;
  const findings =
    sources.workDays === undefined
      ? 0
      : [
          ...sources.workDays.days.flatMap((day) => day.findings),
          ...sources.workDays.periodFindings,
        ].filter(needsAttention).length;
  const projectDays =
    sources.projectDays?.isAvailable === true
      ? sources.projectDays.days.filter((day) => day.isAttributionOpen).length
      : 0;
  const candidates: OpenItem[] = [
    { kind: "corrections", count: ownCorrections, path: routePaths.corrections },
    { kind: "absences", count: ownAbsences, path: routePaths.absences },
    // Own requests are shown at their day in „Meine Zeiten“ (F-07).
    { kind: "attributionRequests", count: ownAttributionRequests, path: routePaths.times },
    { kind: "findings", count: findings, path: routePaths.times },
    { kind: "projectDays", count: projectDays, path: routePaths.times },
  ];
  const items = candidates.filter((item) => item.count > 0);

  return items;
}

/** A figure of the balance card. */
export interface BalanceFigure {
  /** What the figure is. */
  readonly kind: "flexitimeAccount" | "overtimeAccount" | "monthActual" | "monthTarget";
  /** The duration in seconds. */
  readonly seconds: number;
}

/**
 * Chooses the figures of the balance card from `GET /me/balances` as of today: the flexitime and
 * overtime accounts with a time account, otherwise the actual and target time of the month so far.
 *
 * @param balances - The balances as of today.
 * @returns The figures in display order.
 */
export function balanceFigures(balances: BalancesWithBookings): BalanceFigure[] {
  const { account } = balances;
  const figures: BalanceFigure[] =
    balances.hasTimeAccount && account !== null
      ? [
          { kind: "flexitimeAccount", seconds: account.flexitimeSeconds },
          { kind: "overtimeAccount", seconds: account.overtimeSeconds },
        ]
      : [
          { kind: "monthActual", seconds: balances.month.actualSeconds },
          { kind: "monthTarget", seconds: balances.month.targetSeconds },
        ];

  return figures;
}
